import { useState } from 'react';
import dayjs from 'dayjs';
import { Button, Input, Segmented, Switch } from 'antd';
import { STANDARD_ROOMS, ROOM_SUGGESTIONS, CONDITIONS, PERMITS, suggestionsFor, roomsOf, listedCount } from '../lib/inventoryList';
import { termAmount } from '../lib/bookingFormMoney';

// The two documents an Agreement to Purchase / Agreement to Rent carries and that
// are signed on the SAME signature (Leon Lim, 4 Sep 2026):
//
//   InventorySection — optional, one switch. The agent BUILDS the list (owner,
//     2026-10-04: "editable and flexible"): any room, any item, with qty, brand, an
//     optional condition and a note, starting from CID's standard rooms or blank.
//     The buyer/tenant signs it with the offer; the owner/landlord sees their
//     signature and signs it with the acceptance. The negotiator does not sign.
//     InventoryEditor is the list on its own — CID Forms' Inventory List uses it too.
//   OwnerLouSection  — on by default, one switch (form_data.include_owner_lou; the
//     servers default it on a new OTP/OTR and accept the switch only while the
//     agreement is a draft — owner, 2026-10-06). The
//     owner's Letter of Undertaking is filled in from the agreement; the agent adds
//     only the professional fee. Kept under the owner's own prefix (owner_lou_* /
//     landlord_lou_*) so it never reaches the buyer's copy. Numbered from the CID
//     Forms series once the agreement leaves DRAFT (`louRef`).
//
// Mirrored by the admin portal's BookingFormPage and the desktop FormCreate.

const SST_PERCENT = 8;
const rm = (n) => (n == null || Number.isNaN(n) ? '—' : `RM ${Number(n).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

// `bare` drops the phone's section label and card, for a screen that frames the
// section itself (the desktop FormCreate puts each in a titled card of its own).
export function InventorySection({ fd, setFd, rent, bare = false }) {
  const on = !!fd.include_inventory_list;
  const inv = (fd.inventory && typeof fd.inventory === 'object' && !Array.isArray(fd.inventory)) ? fd.inventory : {};
  const buyer = rent ? 'tenant' : 'buyer';
  const owner = rent ? 'landlord' : 'vendor';
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Attach an Inventory List</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.45, marginTop: 3 }}>
            Optional. The {buyer} and the {owner} sign it together with this agreement — on the same signature.
          </div>
        </div>
        <Switch data-demo="inv-toggle" checked={on} onChange={(v) => setFd({ include_inventory_list: v })} />
      </div>
      {on && <InventoryEditor inv={inv} onChange={(next) => setFd({ inventory: next })} />}
    </>
  );
  if (bare) return <div style={stack} data-demo="inv-card">{body}</div>;
  return (
    <>
      <div style={sectionLabel}>INVENTORY LIST</div>
      <div style={card} data-demo="inv-card">{body}</div>
    </>
  );
}

/**
 * The list itself — rooms, items, permits, remarks — editing one `inventory`
 * object ({ rooms, permit_*, remarks }; see lib/inventoryList). Used by the
 * agreement's Inventory List section and by the CID Forms Inventory List.
 */
export function InventoryEditor({ inv: invIn, onChange }) {
  const inv = (invIn && typeof invIn === 'object' && !Array.isArray(invIn)) ? invIn : {};
  const started = Array.isArray(inv.rooms);
  const rooms = roomsOf(inv);
  const [open, setOpen] = useState(null);
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoom, setNewRoom] = useState('');
  const [custom, setCustom] = useState('');
  const count = listedCount(inv);

  const save = (patch) => onChange({ ...inv, ...patch });
  const setRooms = (next) => save({ rooms: next });
  const patchRoom = (ri, p) => setRooms(rooms.map((r, i) => (i === ri ? { ...r, ...p } : r)));
  const itemsOf = (ri) => rooms[ri]?.items || [];
  const patchItem = (ri, ii, p) => patchRoom(ri, { items: itemsOf(ri).map((it, j) => (j === ii ? { ...it, ...p } : it)) });
  const addItem = (ri, name) => { const n = String(name || '').trim(); if (n) patchRoom(ri, { items: [...itemsOf(ri), { name: n, qty: 1 }] }); };
  const removeItem = (ri, ii) => patchRoom(ri, { items: itemsOf(ri).filter((_, j) => j !== ii) });
  const removeRoom = (ri) => { setRooms(rooms.filter((_, i) => i !== ri)); setOpen(null); };
  const addRoom = (name) => {
    const n = String(name || '').trim();
    if (!n) return;
    setRooms([...rooms, { name: n, items: [] }]);
    setOpen(rooms.length);
    setAddingRoom(false);
    setNewRoom('');
  };

  return (
    <>
      {!started && (
        <div data-demo="inv-start" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={fieldLabel}>How do you want to start?</div>
          <Button block type="primary" data-demo="inv-start-standard" onClick={() => { setRooms(STANDARD_ROOMS.map((name) => ({ name, items: [] }))); setOpen(null); }} style={{ height: 'auto', padding: '9px 12px', whiteSpace: 'normal' }}>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.35 }}>
              <b>CID’s standard rooms</b>
              <span style={{ fontSize: 11.5, opacity: 0.85 }}>Living, bedrooms, kitchen, bathroom — add, rename or remove any</span>
            </span>
          </Button>
          <Button block data-demo="inv-start-blank" onClick={() => setRooms([])}>Start blank — add my own rooms</Button>
        </div>
      )}

      {started && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--muted)', marginTop: -2 }}>
            <span>Only rooms with items are printed.</span>
            <span data-demo="inv-count" style={{ ...chip, background: count ? 'var(--chip)' : 'var(--surface2)', color: count ? 'var(--primary)' : 'var(--muted)' }}>{count} item{count === 1 ? '' : 's'}</span>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {rooms.map((r, ri) => {
              const items = r.items || [];
              const n = items.filter((i) => String(i.name || '').trim()).length;
              const isOpen = open === ri;
              const have = new Set(items.map((i) => String(i.name || '').toLowerCase()));
              const quick = suggestionsFor(r.name).filter((s) => !have.has(s.toLowerCase()));
              return (
                <div key={ri} data-demo={`room-${ri}`} style={{ borderTop: ri ? '1px solid var(--border)' : 'none' }}>
                  <button
                    type="button"
                    data-demo={`room-head-${ri}`}
                    onClick={() => { setOpen(isOpen ? null : ri); setCustom(''); }}
                    style={{ all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', cursor: 'pointer', background: isOpen ? 'var(--surface2)' : 'transparent' }}
                  >
                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || 'Unnamed room'}</span>
                    <span style={{ ...chip, background: n ? 'var(--chip)' : 'var(--surface2)', color: n ? 'var(--primary)' : 'var(--muted)' }}>{n}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 12, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                  </button>

                  {isOpen && (
                    <div style={{ padding: '6px 12px 12px', display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--surface2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--muted)', width: 44, flex: 'none' }}>Name</span>
                        <Input data-demo={`room-name-${ri}`} style={{ height: 36 }} value={r.name ?? ''} onChange={(e) => patchRoom(ri, { name: e.target.value })} />
                      </div>

                      {items.map((it, ii) => (
                        <div key={ii} data-demo={`item-${ri}-${ii}`} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 9, display: 'flex', flexDirection: 'column', gap: 7 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <Input data-demo={`item-${ri}-${ii}-name`} style={{ height: 36, fontWeight: 600 }} value={it.name ?? ''} onChange={(e) => patchItem(ri, ii, { name: e.target.value })} placeholder="Item" />
                            <Stepper demo={`item-${ri}-${ii}-qty`} value={it.qty} onChange={(q) => patchItem(ri, ii, { qty: q })} />
                            <button type="button" aria-label="Remove item" data-demo={`item-${ri}-${ii}-del`} onClick={() => removeItem(ri, ii)} style={{ all: 'unset', cursor: 'pointer', width: 26, textAlign: 'center', color: 'var(--muted)', fontSize: 17 }}>×</button>
                          </div>
                          <Input data-demo={`item-${ri}-${ii}-brand`} style={{ height: 34 }} value={it.brand ?? ''} onChange={(e) => patchItem(ri, ii, { brand: e.target.value })} placeholder="Brand / model (optional)" />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Pills demo={`item-${ri}-${ii}-cond`} value={it.cond} onChange={(v) => patchItem(ri, ii, { cond: v })} options={CONDITIONS.map((c) => ({ label: c, value: c }))} />
                            <span style={{ flex: 1 }} />
                            {it.note === undefined && (
                              <a data-demo={`item-${ri}-${ii}-addnote`} onClick={() => patchItem(ri, ii, { note: '' })} style={{ fontSize: 12 }}>+ Note</a>
                            )}
                          </div>
                          {it.note !== undefined && (
                            <Input data-demo={`item-${ri}-${ii}-note`} style={{ height: 34 }} value={it.note} onChange={(e) => patchItem(ri, ii, { note: e.target.value })} placeholder="Note — e.g. door seal worn" />
                          )}
                        </div>
                      ))}

                      {quick.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <span style={{ fontSize: 11.5, color: 'var(--muted)', width: '100%' }}>Quick add</span>
                          {quick.map((s) => (
                            <button key={s} type="button" data-demo={`chip-${ri}-${s}`} onClick={() => addItem(ri, s)} style={chipBtn}>+ {s}</button>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 7 }}>
                        <Input data-demo={`custom-${ri}`} style={{ height: 36 }} value={custom} onChange={(e) => setCustom(e.target.value)} onPressEnter={() => { addItem(ri, custom); setCustom(''); }} placeholder="Any other item…" />
                        <Button data-demo={`custom-add-${ri}`} style={{ height: 36 }} onClick={() => { addItem(ri, custom); setCustom(''); }}>Add</Button>
                      </div>
                      <a data-demo={`room-remove-${ri}`} onClick={() => removeRoom(ri)} style={{ fontSize: 12, color: 'var(--bad)', alignSelf: 'flex-start' }}>Remove this room</a>
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ borderTop: rooms.length ? '1px solid var(--border)' : 'none', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!addingRoom ? (
                <a data-demo="add-room" onClick={() => setAddingRoom(true)} style={{ fontSize: 13, fontWeight: 600 }}>+ Add a room</a>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ROOM_SUGGESTIONS.filter((s) => !rooms.some((r) => String(r.name).toLowerCase() === s.toLowerCase())).map((s) => (
                      <button key={s} type="button" data-demo={`addroom-${s}`} onClick={() => addRoom(s)} style={chipBtn}>+ {s}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <Input data-demo="addroom-custom" style={{ height: 36 }} value={newRoom} onChange={(e) => setNewRoom(e.target.value)} onPressEnter={() => addRoom(newRoom)} placeholder="Or type a room name…" />
                    <Button style={{ height: 36 }} onClick={() => addRoom(newRoom)}>Add</Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={fieldLabel}>Approval permit</div>
            {PERMITS.map(([k, label]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 12.5 }}>{label}</span>
                <Pills value={inv[k]} onChange={(v) => save({ [k]: v })} options={[{ label: 'With permit', value: 'with' }, { label: 'Without', value: 'without' }]} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={fieldLabel}>Remarks</div>
            <Input.TextArea rows={2} value={inv.remarks ?? ''} onChange={(e) => save({ remarks: e.target.value })} placeholder="Keys handed over, meter readings…" />
          </div>
        </>
      )}
    </>
  );
}

// Tap to choose, tap again to clear — and nothing is shown as chosen until it is.
// (An antd Segmented with no value highlights its first option, which on a legal
// document reads as a decision nobody made.)
const Pills = ({ value, onChange, options, demo }) => (
  <div data-demo={demo} style={{ display: 'flex', gap: 5, flex: 'none' }}>
    {options.map((o) => {
      const on = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(on ? '' : o.value)}
          style={{ all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`, background: on ? 'var(--primary)' : 'var(--surface)', color: on ? '#fff' : 'var(--muted)' }}
        >{o.label}</button>
      );
    })}
  </div>
);

const Stepper = ({ value, onChange, demo }) => {
  const q = Math.max(1, Number(value) || 1);
  const b = { all: 'unset', cursor: 'pointer', width: 26, textAlign: 'center', fontSize: 16, fontWeight: 700, color: 'var(--primary)' };
  return (
    <div style={{ flex: 'none', display: 'flex', alignItems: 'center', height: 36, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
      <button type="button" aria-label="Fewer" data-demo={`${demo}-minus`} onClick={() => onChange(Math.max(1, q - 1))} style={b}>−</button>
      <span data-demo={demo} style={{ minWidth: 20, textAlign: 'center', fontWeight: 700, fontSize: 13.5 }}>{q}</span>
      <button type="button" aria-label="More" data-demo={`${demo}-plus`} onClick={() => onChange(q + 1)} style={b}>+</button>
    </div>
  );
};

export function OwnerLouSection({ fd, setFd, rent, formRef, louRef, bare = false }) {
  // Off: switched off by the agent, or an agreement from before the LOU existed.
  const on = fd.include_owner_lou === true;
  const p = rent ? 'landlord_lou_' : 'owner_lou_';
  const set = (k, v) => setFd({ [`${p}${k}`]: v });
  const get = (k) => fd[`${p}${k}`];
  // The fee and the disbursements are figures of 0 or more. A keypad that offers a
  // minus sign (Android) or a paste can still send one, so anything else is blanked
  // rather than stored — the fee then reads as missing, which is what it is.
  const setFigure = (k, v) => set(k, num(v) == null ? '' : v);
  // A fee is never negative or exponent-shaped: stop the keys at the source rather
  // than blanking the figure half-way through typing it.
  const noSign = (e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); };
  const owner = rent ? 'Landlord' : 'Vendor';
  const side = rent ? 'landlord' : 'owner';
  const ownerName = [fd[`${side}_1_name`], fd[`${side}_2_name`]].filter(Boolean).join(' & ');

  // Same arithmetic the server prints (utils/agreementAttachments.ownerLouView).
  const base = num(rent ? fd.rental_rm : fd.selling_price_rm);
  const isAmount = !rent && get('fee_basis') === 'AMOUNT';
  let fee = null;
  if (rent) { const m = num(get('fee_months')); if (base != null && m != null) fee = base * m; }
  else if (isAmount) fee = num(get('fee_amount_rm'));
  else { const pct = num(get('fee_percent')); if (base != null && pct != null) fee = (base * pct) / 100; }
  const sstOn = get('sst') !== 'ABSORBED';
  const sst = sstOn && fee != null ? (fee * SST_PERCENT) / 100 : null;
  const disb = num(get('disbursements_rm'));
  const total = fee == null ? null : fee + (sst || 0) + (disb || 0);
  const earnest = termAmount(fd, 'earnest_deposit', rent ? 'RENT' : 'SALES', base || 0);

  const from = [
    ['Property', fd.property_address],
    ['LOU no.', louRef || `${rent ? 'LLU' : 'VLU'}-… given when sent for signing`],
    ['Attached to', formRef || 'this agreement'],
    ['Dated', fd.date ? dayjs(fd.date).format('D MMM YYYY') : null],
    ['Earnest deposit', earnest ? rm(earnest) : null],
    ['Stakeholder', fd.stakeholder],
    ['Paid by', [fd.payment_type, fd.payment_ref_no].filter(Boolean).join(' · ') || null],
    [`${owner} name & IC`, ownerName || `typed in by the ${owner.toLowerCase()}`],
  ];

  const toggle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Include the Letter of Undertaking</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.45, marginTop: 3 }}>
          {on
            ? <>The {owner.toLowerCase()}(s) sign it together with the acceptance — same signature. It is not shown to the {rent ? 'tenant' : 'purchaser'}.</>
            : <>Switched off. This agreement goes out without the {owner.toLowerCase()}&rsquo;s LOU, and no professional fee is needed.</>}
        </div>
      </div>
      <Switch data-demo="lou-toggle" checked={on} onChange={(v) => setFd({ include_owner_lou: v })} aria-label="Include the Letter of Undertaking" />
    </div>
  );

  const body = !on ? toggle : (
    <>
      {toggle}

      <div data-demo="lou-auto" style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>FILLED IN FROM THIS AGREEMENT</div>
        {from.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.5, padding: '2px 0' }}>
            <span style={{ width: 112, flex: 'none', color: 'var(--muted)' }}>{label}</span>
            <span style={{ flex: 1, minWidth: 0, fontWeight: value ? 600 : 400, color: value ? 'var(--text)' : 'var(--muted)' }}>{value || '—'}</span>
          </div>
        ))}
      </div>

      <div style={fieldLabel}><span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>Professional fee — the only thing you add</div>
      {rent ? (
        <Input data-demo="lou-fee" type="number" min={0} inputMode="decimal" onKeyDown={noSign} value={get('fee_months') ?? ''} onChange={(e) => setFigure('fee_months', e.target.value)} suffix="month’s rental" placeholder="e.g. 1" />
      ) : (
        <>
          <Segmented block value={get('fee_basis') || 'PERCENT'} onChange={(v) => set('fee_basis', v)} options={[{ label: '% of price', value: 'PERCENT' }, { label: 'Fixed RM', value: 'AMOUNT' }]} />
          {isAmount
            ? <Input data-demo="lou-fee" type="number" min={0} inputMode="decimal" onKeyDown={noSign} value={get('fee_amount_rm') ?? ''} onChange={(e) => setFigure('fee_amount_rm', e.target.value)} prefix="RM" placeholder="e.g. 15000" />
            : <Input data-demo="lou-fee" type="number" min={0} inputMode="decimal" onKeyDown={noSign} value={get('fee_percent') ?? ''} onChange={(e) => setFigure('fee_percent', e.target.value)} suffix="% of the purchase price" placeholder="e.g. 2" />}
        </>
      )}
      <div style={fieldLabel}>SST {SST_PERCENT}% on the fee</div>
      <Segmented block value={get('sst') || 'CHARGED'} onChange={(v) => set('sst', v)} options={[{ label: 'Charged', value: 'CHARGED' }, { label: 'Absorbed by agent', value: 'ABSORBED' }]} />
      <div style={fieldLabel}>Disbursements (optional)</div>
      <Input data-demo="lou-disb" type="number" min={0} inputMode="decimal" onKeyDown={noSign} value={get('disbursements_rm') ?? ''} onChange={(e) => setFigure('disbursements_rm', e.target.value)} prefix="RM" placeholder="0" />

      <div data-demo="lou-summary" style={{ borderTop: '1px dashed var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12.5 }}>
        <Row k="Professional fee" v={rm(fee)} />
        {sstOn && <Row k={`SST ${SST_PERCENT}%`} v={rm(sst)} />}
        {disb ? <Row k="Disbursements" v={rm(disb)} /> : null}
        <Row k={`The ${owner.toLowerCase()} undertakes to pay`} v={rm(total)} bold />
      </div>
    </>
  );
  if (bare) return <div style={stack} data-demo="lou-card">{body}</div>;
  return (
    <>
      <div style={sectionLabel}>{owner.toUpperCase()}&rsquo;S LETTER OF UNDERTAKING</div>
      <div style={card} data-demo="lou-card">{body}</div>
    </>
  );
}

const Row = ({ k, v, bold }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontWeight: bold ? 700 : 400, color: bold ? 'var(--text)' : 'var(--muted)' }}>
    <span>{k}</span><span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{v}</span>
  </div>
);

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
// The card's own spacing without the card — a `bare` section inside someone else's frame.
const stack = { display: 'flex', flexDirection: 'column', gap: 14 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginBottom: -6, marginTop: 2 };
const fieldLabel = { fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' };
const chip = { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 };
const chipBtn = { all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--primary)' };
